import type { ReactNode } from 'react';

export type ScoreCtaVariant = 'xml' | 'pdf';

/** How far into a score a demo generation reaches. Mirrors backend
 *  caps (`generation/service.py`: 18 measures for XML) and the mobile
 *  app's `DEMO_SYSTEM_LIMIT` of 3. */
export const DEMO_REACH: Record<ScoreCtaVariant, string> = {
  xml: 'First 18 measures',
  pdf: 'First 3 score systems',
};

export const SCORE_CTA_PRIMARY_BUTTON_CLASS = `
  inline-flex cursor-pointer items-center justify-center gap-2
  rounded-full bg-yellow-400 px-3.5 py-1.5 text-sm font-semibold
  text-gray-800 transition-colors duration-100
  hover:bg-yellow-500
  active:scale-[0.985]
  md:px-5 md:py-2.5 md:text-base
  lg:px-6 lg:py-3.5 lg:text-lg
`;

/**
 * Shared title / subtitle / action stack used by the sample, guest,
 * and signed-in demo CTAs.
 */
export const ScoreCtaInner = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) => (
  <div
    className={`
      mx-auto flex max-w-xl flex-col items-center gap-2.5 text-center
      md:max-w-2xl md:gap-4
      lg:max-w-3xl lg:gap-5
    `}
  >
    <p
      className={`
        text-sm font-medium text-gray-800
        md:text-xl
        lg:text-2xl lg:font-semibold
      `}
    >
      {title}
    </p>
    <p className="text-sm text-gray-500 md:text-base lg:text-lg">{subtitle}</p>
    {children}
  </div>
);

/**
 * White card closing the score column. Passed as ScoreViewport's
 * `footer` so zoom does not magnify it. Same card at every
 * breakpoint — not a strip on the mobile bottom bar.
 */
export const ScoreCtaBox = ({ children }: { children: ReactNode }) => (
  <div
    className={`
      mb-10 w-11/12 min-w-11/12 rounded-xl border border-gray-200 bg-white px-4
      py-5
      shadow-[0_-10px_15px_-3px_rgb(0_0_0/0.1),0_-4px_6px_-4px_rgb(0_0_0/0.1)]
      md:rounded-2xl md:px-8 md:py-8
      lg:px-10 lg:py-10
    `}
  >
    {children}
  </div>
);
